// Host lobby-settings form: draft <-> settings, and the minimal patch to send (pure).
import type { LobbySettings, LobbySettingsPatch, SpellRule } from "@wp/shared";
import { knownOverrides } from "./spellbook";

export interface SettingsDraft {
  ante: string;
  turnSeconds: string;
  revealSeconds: string;
  spectatorsAllowed: boolean;
  hideCardsFromSpectators: boolean;
  unlimited: boolean;
  handLimit: string;
  spellRules: Record<string, SpellRule>;
}

export function toDraft(s: LobbySettings): SettingsDraft {
  return {
    ante: String(s.ante),
    turnSeconds: String(s.turnSeconds),
    revealSeconds: String(s.revealSeconds),
    spectatorsAllowed: s.spectatorsAllowed,
    hideCardsFromSpectators: s.hideCardsFromSpectators,
    unlimited: s.handLimit === null,
    handLimit: String(s.handLimit ?? 20),
    spellRules: knownOverrides(s.spellRules),
  };
}

const intOrNaN = (s: string) => (/^\d{1,6}$/.test(s.trim()) ? Number(s.trim()) : Number.NaN);

function sameRules(a: Record<string, SpellRule>, b: Record<string, SpellRule>): boolean {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => a[k] === b[k]);
}

/** Only the fields that changed (the server applies a partial patch). */
export function settingsPatch(current: LobbySettings, d: SettingsDraft): LobbySettingsPatch {
  const patch: LobbySettingsPatch = {};
  const ante = intOrNaN(d.ante);
  const turn = intOrNaN(d.turnSeconds);
  const reveal = intOrNaN(d.revealSeconds);
  const limit = d.unlimited ? null : intOrNaN(d.handLimit);
  if (ante !== current.ante) patch.ante = ante;
  if (turn !== current.turnSeconds) patch.turnSeconds = turn;
  if (reveal !== current.revealSeconds) patch.revealSeconds = reveal;
  if (d.spectatorsAllowed !== current.spectatorsAllowed) patch.spectatorsAllowed = d.spectatorsAllowed;
  if (d.hideCardsFromSpectators !== current.hideCardsFromSpectators) {
    patch.hideCardsFromSpectators = d.hideCardsFromSpectators;
  }
  if (limit !== current.handLimit) patch.handLimit = limit;
  if (!sameRules(d.spellRules, knownOverrides(current.spellRules))) patch.spellRules = d.spellRules;
  return patch;
}
